import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import { Box } from '@mui/material';

import { InfoCard } from 'src/components/common/infocard/InfoCard';
import { IInfoColumn } from 'src/components/common/types/infocardTypes';
import PageWithLayout from 'src/components/layout/PageWithLayout';
import { IUserProfileState } from 'src/features/profile';
import ChangePasswordForm from 'src/features/profile/components/ChangePasswordForm';
import { EditProfileDrawer } from 'src/features/profile/components/EditProfileDrawer';
import { getUserProfile } from 'src/features/profile/store/userProfileThunks';
import { USER_PROFILE_LOADING_KEY } from 'src/features/profile/store/userProfileThunks';
import { RootState, AppDispatch } from 'src/store';
import { formatTimestamp } from 'src/utils/dateUtils';
import { snakeToTitleCase } from 'src/utils/formatter';

const UserProfilePage: React.FC = (): React.JSX.Element => {
	const dispatch = useDispatch<AppDispatch>();
	const { profile } = useSelector(
		(state: RootState) => state.user_profile as IUserProfileState,
	);
	const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);

	const breadcrumbs = [{ label: 'Profile Settings' }];
	const infoColumns: IInfoColumn[] = [
		{
			label: 'Email',
			value: profile?.email || '-',
		},
		{
			label: 'Type',
			value: profile?.type ? snakeToTitleCase(profile?.type) : '-',
		},
		{
			label: 'Status',
			value: profile?.status ? snakeToTitleCase(profile?.status) : '-',
		},
		{
			label: 'Created At',
			value: profile?.created_at
				? formatTimestamp(profile.created_at)
				: '-',
		},
	];

	useEffect(() => {
		if (!profile) {
			dispatch(getUserProfile({}));
		}
	}, [dispatch, profile]);

	return (
		<PageWithLayout breadcrumbs={breadcrumbs}>
			<Box
				className='flex flex-col gap-6 lg:w-[50vw] mx-auto max-md:w-full mb-4'
				sx={{ mt: 4 }}
			>
				<InfoCard
					title={
						profile
							? `${profile.first_name} ${profile.last_name}`
							: 'Loading...'
					}
					columns={infoColumns}
					loadingKey={USER_PROFILE_LOADING_KEY}
					contextMenuItems={[
						{
							label: 'Edit Profile',
							icon: <ModeEditOutlinedIcon />,
							onClick: () => setIsEditDrawerOpen(true),
						},
					]}
				/>

				{profile && (
					<EditProfileDrawer
						open={isEditDrawerOpen}
						onClose={() => setIsEditDrawerOpen(false)}
					/>
				)}

				<ChangePasswordForm overRideLoading={isEditDrawerOpen} />
			</Box>
		</PageWithLayout>
	);
};

export default UserProfilePage;
